import React, { useId, useRef, useState } from 'react';
import Accordion from '../../../../shared/components/Accordion/Accordion';
import Button from '../../../../shared/components/Button/Button';
import Drawer from '../../../../shared/components/Drawer/Drawer';
import Input from '../../../../shared/components/Input/Input';
import Select from '../../../../shared/components/Select/Select';
import Typography from '../../../../shared/components/Typography/Typography';
import useWikiAutosave from '../hooks/useWikiAutosave';
import { formatWikiTime } from '../wiki.date';
import { buildEditorPayload, getWikiUserChangePayload, normalizeTiptapDocument, stableSerialize } from '../services/wikiContentSerializer';
import WikiTiptapEditor from './editor/WikiTiptapEditor';
import WikiAttachmentSelector from './WikiAttachmentSelector';
import WikiRelatedArticleSelector from './WikiRelatedArticleSelector';
import { ArrowBackIcon } from './WikiIcons';
import {
  parseIdList,
  statuses,
} from '../wiki.constants';

const formatAutosaveTime = (value) => {
  if (!value) {
    return '';
  }

  const time = formatWikiTime(value);
  return time ? `Enregistré à ${time}` : '';
};

function WikiEditor({
  article,
  articles = [],
  categories: editorCategories = [],
  articleTypes: editorArticleTypes = [],
  getCategoryLabel,
  onAutosave,
  onCancel,
  onSave,
}) {
  const settingsDrawerTitleId = useId();
  const previewDrawerTitleId = useId();
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({
    id: article?.id || null,
    title: article?.title || '',
    category: article?.category || editorCategories[0]?.slug || 'gestion-projet',
    type: article?.type || editorArticleTypes[0]?.name || 'Procédure',
    owner: article?.owner || 'Equipe',
    status: article?.status || 'Brouillon',
    version: article?.version || '0.1',
    tags: article?.tags || '',
    summary: article?.summary || '',
    changeSummary: '',
    editVersion: article?.editVersion || 1,
    relatedArticleText: (article?.relatedArticles || []).join(', '),
    attachments: article?.attachments || [],
    contentJson: normalizeTiptapDocument(article?.contentJson || article?.plainText || article?.content || ''),
    renderedHtml: article?.renderedHtml || article?.content || '',
    plainText: article?.plainText || '',
    content: article?.renderedHtml || article?.content || '',
  }));
  const [editorError, setEditorError] = useState('');

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const buildPayload = (status = draft.status) => ({
    ...draft,
    categoryId: editorCategories.find((category) => category.slug === draft.category)?.id || null,
    typeId: editorArticleTypes.find((type) => type.name === draft.type)?.id || null,
    status,
    relatedArticles: parseIdList(draft.relatedArticleText),
    attachments: draft.attachments,
  });
  const initialSignature = useRef(stableSerialize(getWikiUserChangePayload(draft)));
  const autosavePayload = buildPayload(draft.status);
  const autosave = useWikiAutosave({
    enabled: Boolean(draft.id),
    payload: autosavePayload,
    onSave: async (payload) => {
      const updated = await onAutosave(payload);
      setDraft((current) => ({
        ...current,
        editVersion: updated?.editVersion || current.editVersion,
        updatedAt: updated?.updatedAt || current.updatedAt,
      }));
      return updated;
    },
  });

  const updateEditorContent = (editor) => {
    try {
      const contentPayload = buildEditorPayload(editor);
      setEditorError('');
      setDraft((current) => ({ ...current, ...contentPayload }));
    } catch (error) {
      setEditorError(error.message || 'Contenu Wiki invalide.');
    }
  };

  const hasDraftChanged = stableSerialize(getWikiUserChangePayload(draft)) !== initialSignature.current;
  const hasUnsavedChanges = hasDraftChanged
    && (!draft.id || ['dirty', 'saving', 'error', 'conflict'].includes(autosave.state));
  const savedAtLabel = formatAutosaveTime(autosave.savedAt);
  const autosaveLabel = {
    dirty: 'Modifications non enregistrées',
    saving: 'Sauvegarde en cours',
    saved: savedAtLabel || 'Enregistré',
    error: 'Erreur de sauvegarde',
    conflict: 'Conflit de version',
  }[autosave.state] || 'Prêt';

  const cancelEditor = () => {
    if (
      hasUnsavedChanges
      && !window.confirm('Des modifications ne sont pas enregistrées. Quitter l\'éditeur ?')
    ) {
      return;
    }
    onCancel();
  };
  const renderSettings = () => (
    <>
      <Typography variant="h6">Panneau documentaire</Typography>
      <Accordion title="Proprietes" helper="Categorie, type, referent et tags" defaultOpen>
        <Select
          fullWidth
          label="Catégorie"
          value={draft.category}
          onChange={(event) => updateDraft('category', event.target.value)}
          options={editorCategories.map((category) => ({ value: category.slug, label: category.name }))}
        />
        <Select
          fullWidth
          label="Type"
          value={draft.type}
          onChange={(event) => updateDraft('type', event.target.value)}
          options={editorArticleTypes.map((type) => ({ value: type.name, label: type.name }))}
        />
        <Input fullWidth label="Referent" value={draft.owner} onChange={(event) => updateDraft('owner', event.target.value)} />
        <Input fullWidth label="Tags" value={draft.tags} onChange={(event) => updateDraft('tags', event.target.value)} placeholder="DOE, Projet, GED" />
      </Accordion>

      <Accordion title="Publication" helper="Statut, version et résumé de modification" defaultOpen>
        <Select
          fullWidth
          label="Statut"
          value={draft.status}
          onChange={(event) => updateDraft('status', event.target.value)}
          options={statuses.map((status) => ({ value: status, label: status }))}
        />
        <Input fullWidth label="Version" value={draft.version} onChange={(event) => updateDraft('version', event.target.value)} />
        <Input fullWidth label="Résumé de modification" value={draft.changeSummary} onChange={(event) => updateDraft('changeSummary', event.target.value)} />
      </Accordion>

      <Accordion title="Relations" helper="Articles reliés à ce document" defaultOpen>
        <WikiRelatedArticleSelector
          articles={articles}
          currentArticleId={draft.id}
          getCategoryLabel={getCategoryLabel}
          value={draft.relatedArticleText}
          onChange={(nextValue) => updateDraft('relatedArticleText', nextValue)}
        />
      </Accordion>

      <Accordion title="Documents" helper="Pièces GED du dossier Wiki">
        <WikiAttachmentSelector value={draft.attachments} onChange={(attachments) => updateDraft('attachments', attachments)} />
      </Accordion>
    </>
  );

  return (
    <div className="wiki-editor-page">
      <div className="wiki-editor-header">
        <Button variant="tertiary" iconStart={<ArrowBackIcon fontSize="small" />} onClick={cancelEditor}>
          Retour au Wiki
        </Button>
        <div className="wiki-editor-header-title">
          <Typography variant="h4">{article ? "Modifier l'article" : 'Nouvel article'}</Typography>
          <span className={`wiki-editor-header-status ${autosave.state}`} role="status" aria-live="polite">
            {autosaveLabel}
          </span>
        </div>
        <div className="wiki-editor-actions">
          <Button className="wiki-editor-settings-button" variant="tertiary" onClick={() => setSettingsDrawerOpen(true)}>
            Propriétés
          </Button>
          <Button variant="tertiary" onClick={() => setPreviewDrawerOpen(true)}>Aperçu</Button>
          <Button variant="secondary" onClick={() => onSave(buildPayload('Brouillon'))}>Enregistrer</Button>
          <Button variant="primary" onClick={() => onSave(buildPayload('Publie'))}>Publier</Button>
        </div>
      </div>

      <div className="wiki-editor-layout">
        <section className="wiki-editor-main">
          <Input className="wiki-document-title-input" fullWidth label="Titre" value={draft.title} onChange={(event) => updateDraft('title', event.target.value)} placeholder="Titre de l'article" />
          <Input className="wiki-document-summary-input" fullWidth label="Résumé" value={draft.summary} onChange={(event) => updateDraft('summary', event.target.value)} multiline rows={3} />
          <WikiTiptapEditor
            autosave={autosave}
            error={editorError}
            value={draft.contentJson}
            onChange={updateEditorContent}
          />
        </section>

        <aside className="wiki-editor-settings">
          {renderSettings()}
        </aside>
      </div>

      <Drawer
        labelledBy={settingsDrawerTitleId}
        open={settingsDrawerOpen}
        placement="right"
        title="Proprietes"
        onClose={() => setSettingsDrawerOpen(false)}
      >
        <div className="wiki-editor-settings wiki-editor-settings--drawer">
          {renderSettings()}
        </div>
      </Drawer>

      <Drawer
        className="wiki-editor-preview-drawer"
        labelledBy={previewDrawerTitleId}
        open={previewDrawerOpen}
        placement="right"
        title="Apercu de l'article"
        onClose={() => setPreviewDrawerOpen(false)}
      >
        <div className="wiki-editor-preview">
          <header className="wiki-editor-preview-header">
            <Typography variant="h4">{draft.title || 'Article sans titre'}</Typography>
            {draft.summary && <p>{draft.summary}</p>}
          </header>
          <WikiTiptapEditor readOnly value={draft.contentJson} />
        </div>
      </Drawer>
    </div>
  );
}

export default WikiEditor;
